/**
 * Map a pathname to a permanent nav key and a human breadcrumb like
 * "HQ → Sales Tools → Call Centre Booking".
 *
 * This is the layer that keeps a note meaningful after a redesign — menu keys
 * outlive URL changes, so grouping still works after a restructure.
 *
 * PropCloudAI's menu config (menuPermissionConfig.ts) keys most sub-items by
 * `action` (floating windows) rather than `path`, so the resolver layers:
 *   1. path-bearing sub-items from COMPLETE_MENU_STRUCTURE (most specific),
 *   2. a static table of the top-level route groups (dashboard cards + major
 *      screens that have no menu entry),
 * with longest-prefix-wins matching, query strings ignored. A route neither
 * layer knows still gets a usable label from its routePattern.
 */
import { COMPLETE_MENU_STRUCTURE } from '../../config/menuPermissionConfig';

export interface NavAnchorResult {
  navKey: string;
  /** Breadcrumb of group labels + leaf label, joined with " → ". */
  navLabel: string;
}

/**
 * Top-level screens — mirrors the dashboard cards' paths (the cards now live
 * in config/adminNavCards.ts; this hand-kept table predates that) plus major
 * route groups with no menu entry. Order does not matter; longest prefix wins.
 */
const ROUTE_LABELS: Array<{ prefix: string; navKey: string; navLabel: string }> = [
  { prefix: '/dashboard', navKey: 'dashboard', navLabel: 'Dashboard' },
  { prefix: '/propcloud-hq', navKey: 'hq', navLabel: 'PropCloud HQ' },
  { prefix: '/propcloud-partner', navKey: 'partner', navLabel: 'PropCloud Partner' },
  { prefix: '/hq-management', navKey: 'hq.management', navLabel: 'HQ Management' },
  { prefix: '/crm-v2', navKey: 'crm', navLabel: 'CRM' },
  { prefix: '/crm', navKey: 'crm.legacy', navLabel: 'CRM (legacy)' },
  { prefix: '/proposals', navKey: 'investmentProposals', navLabel: 'Investment Proposals' },
  { prefix: '/advertising', navKey: 'advertising', navLabel: 'Advertising' },
  { prefix: '/feedback', navKey: 'feedback', navLabel: 'Report an Issue' },
  { prefix: '/graphic-design', navKey: 'graphicDesign', navLabel: 'Graphic Design' },
  { prefix: '/viewing-oversight', navKey: 'viewingOversight', navLabel: 'Viewing Oversight' },
  { prefix: '/viewing', navKey: 'viewing', navLabel: 'Viewings' },
  { prefix: '/consultations', navKey: 'consultations', navLabel: 'Consultations' },
  { prefix: '/training', navKey: 'training', navLabel: 'Training' },
  { prefix: '/my-documents', navKey: 'myDocuments', navLabel: 'My Documents' },
  { prefix: '/my-tickets', navKey: 'support.myTickets', navLabel: 'Support → My Tickets' },
  { prefix: '/accounting', navKey: 'accounting', navLabel: 'Accounting' },
  { prefix: '/users', navKey: 'users', navLabel: 'User Management' },
  { prefix: '/roles-permissions', navKey: 'rolesPermissions', navLabel: 'Roles & Permissions' },
  { prefix: '/commission-settings', navKey: 'commissionSettings', navLabel: 'Commission Settings' },
  { prefix: '/reports', navKey: 'reports', navLabel: 'Reports' },
  { prefix: '/stats', navKey: 'stats', navLabel: 'Statistics' },
  { prefix: '/deals', navKey: 'deals', navLabel: 'Deals' },
  { prefix: '/payments', navKey: 'payments', navLabel: 'Payments' },
  { prefix: '/expired-reservations', navKey: 'expiredReservations', navLabel: 'Expired Reservations' },
  { prefix: '/admin/tickets', navKey: 'admin.supportHub', navLabel: 'Admin → Support Hub' },
  { prefix: '/admin', navKey: 'admin', navLabel: 'Admin' },
];

export function resolveNavAnchor(pathname: string): NavAnchorResult | null {
  const path = pathname.split('?')[0];
  let best: { key: string; label: string; length: number } | undefined;

  const consider = (target: string, key: string, label: string): void => {
    if (!target) return;
    const clean = target.split('?')[0];
    const matches = path === clean || path.startsWith(`${clean}/`);
    if (matches && (best === undefined || clean.length > best.length)) {
      best = { key, label, length: clean.length };
    }
  };

  // Layer 1 — path-bearing menu sub-items (most specific labels available).
  // COMPLETE_MENU_STRUCTURE nests card → { groupId: MenuGroup }.
  for (const card of Object.values(COMPLETE_MENU_STRUCTURE)) {
    for (const group of Object.values(card)) {
      for (const item of group.subItems ?? []) {
        if (item.path) consider(item.path, item.id, `${group.name} → ${item.name}`);
      }
    }
  }

  // Layer 2 — top-level route groups.
  for (const entry of ROUTE_LABELS) {
    consider(entry.prefix, entry.navKey, entry.navLabel);
  }

  return best ? { navKey: best.key, navLabel: best.label } : null;
}

/**
 * Best-effort description of what sits at a viewport point — the nearest
 * useful text and a short CSS path. Never throws; a note without this is
 * still fully usable.
 */
export function describeElementAt(
  xPct: number,
  yPct: number,
): { elementText: string | null; elementPath: string | null } {
  try {
    const el = document.elementFromPoint(
      xPct * window.innerWidth,
      yPct * window.innerHeight,
    );
    if (!el) return { elementText: null, elementPath: null };

    // Nearest meaningful text: the element's own, else the closest labelled
    // ancestor (button/link/heading), truncated to stay a label not a dump.
    const own = (el.textContent ?? '').trim().replace(/\s+/g, ' ');
    const labelled = el.closest('button, a, h1, h2, h3, th, label');
    const context = (labelled?.textContent ?? '').trim().replace(/\s+/g, ' ');
    const text = (own || context).slice(0, 120) || null;

    // Short structural path — tag.class chain, 4 levels, ids preferred.
    const parts: string[] = [];
    let cur: Element | null = el;
    while (cur && cur.tagName !== 'BODY' && parts.length < 4) {
      if (cur.id) {
        parts.unshift(`#${cur.id}`);
        break;
      }
      const cls = [...cur.classList].slice(0, 2).join('.');
      parts.unshift(cls ? `${cur.tagName.toLowerCase()}.${cls}` : cur.tagName.toLowerCase());
      cur = cur.parentElement;
    }
    return { elementText: text, elementPath: parts.join(' > ') || null };
  } catch {
    return { elementText: null, elementPath: null };
  }
}
